import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Check,
  Cloud,
  Headset,
  Palette,
  CreditCard,
  Infinity as InfinityIcon,
  Layers,
  Zap,
  Rocket,
  Users,
  IndianRupee,
  Tags,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import SEO, { SITE_URL } from "../SEO";
import { FaqSection } from "../FaqList";
import { AGENCY_OS_FAQS } from "../../data/pageFaqs";
import { Reveal, Overline } from "../Reveal";
import { Button } from "../ui/button";
import FloatingIcons from "../FloatingIcons";
import ProductBadge from "../ProductBadge";
import FeatureArt from "../FeatureArt";
import { SIGNUP_URL, ADDONS } from "../../data/content";
import { PRODUCTS } from "../../data/products";
import { FEATURE_CATEGORIES, featurePath } from "../../data/features";
import { PRODUCT_FAMILIES, PRODUCTS_KB } from "../../data/knowledge";
import { ORGANIZATION_ID } from "../../lib/schema";

// Everything below is read from the shared data files, so this page always
// lists the same products and capabilities as /features and the product
// pages - and every card links to its product page.

const featureOf = (kb) => FEATURE_CATEGORIES.find((c) => c.id === kb.featureId) || null;

// Icon/colour for a product: its feature page accent, or (for products with
// no page yet) the matching card in products.js.
const badgeOf = (kb) => {
  const f = featureOf(kb);
  if (f) return f.accent;
  const legacy = PRODUCTS.find((p) => p.name === kb.name);
  return legacy ? { Icon: legacy.Icon, color: legacy.color, tint: legacy.tint } : { Icon: Layers, color: "#FF6B00", tint: "rgba(255,107,0,0.10)" };
};

// Products clients use (the agency platform itself is the toolkit below).
const PRODUCT_GROUPS = PRODUCT_FAMILIES.filter((f) => f.id !== "agency-platform").map((fam) => ({
  ...fam,
  items: PRODUCTS_KB.filter((p) => p.family === fam.id && p.status === "ACTIVE"),
}));
const CLIENT_PRODUCTS = PRODUCT_GROUPS.flatMap((g) => g.items);

// The agency's own toolkit = the three agency-platform feature pages.
const TOOLKIT = ["agency", "publishing", "ai-tools"].map((id) => FEATURE_CATEGORIES.find((c) => c.id === id));

const LAUNCH_KIT = ADDONS.find((a) => a.title === "Platform & Setup");

const STEPS = [
  {
    icon: Tags,
    title: "Choose a plan",
    desc: "Pick a Channel Based or Unlimited plan - paid yearly, monthly or once for lifetime.",
    link: { to: "/pricing", label: "Compare plans" },
  },
  {
    icon: Palette,
    title: "Brand it as yours",
    desc: "Your name, logo, favicon, colours and login page on your own domain, with a separate domain for QR pages. Branding and domain setup are done for you.",
    link: { to: featurePath(FEATURE_CATEGORIES.find((c) => c.id === "agency")), label: "White-label details" },
  },
  {
    icon: Users,
    title: "Add clients and set plans",
    desc: "Create client accounts, open any client's panel in one click, and build your own pricing tiers with usage limits.",
    link: { to: "/product-finder", label: "Find the right products for a client" },
  },
  {
    icon: IndianRupee,
    title: "Sell and bill",
    desc: "Find prospects with weak Google profiles, run email outreach, then bill clients with recurring invoices and track MRR, ARPU and churn.",
    link: { to: "/how-agencies-earn", label: "See how agencies earn" },
  },
];

const BENEFITS = [
  {
    icon: Cloud,
    title: "100% Cloud Hosted",
    desc: "Fully managed, always-on infrastructure. No servers to maintain - ever.",
  },
  {
    icon: Headset,
    title: "Dedicated Support",
    desc: "White-glove onboarding and ongoing support for you and your clients.",
  },
  {
    icon: Palette,
    title: "Your Brand & Domain",
    desc: "Custom logo, colors and domain. Clients only ever see you - never us.",
  },
  {
    icon: CreditCard,
    title: "Client Billing & Seats",
    desc: "Set your own prices, manage clients and seats, and keep 100% of the margin.",
  },
  {
    icon: InfinityIcon,
    title: "Infinitely Scalable",
    desc: "From 5 to 5,000 clients on the very same platform, no re-architecting.",
  },
  {
    icon: Layers,
    title: "All Products, One Login",
    desc: `${CLIENT_PRODUCTS.length} products plus the agency toolkit, bundled into a single dashboard.`,
  },
];

const EXPLORE = [
  { to: "/pricing", title: "Pricing", desc: "Channel Based and Unlimited plans, monthly, yearly or lifetime." },
  { to: "/how-agencies-earn", title: "How agencies earn", desc: "Resale pricing examples and a revenue calculator." },
  { to: "/white-label-ai-saas-lifetime-deal", title: "Lifetime deal", desc: "Own an Unlimited plan with one payment." },
  { to: "/features", title: "All features", desc: "Every product and capability in one place." },
  { to: "/product-finder", title: "Product Finder", desc: "Match a client's problem to the right product." },
  { to: "/faq#white-label", title: "White-label FAQ", desc: "Branding, hosting, support and plan control." },
];

// Service + product-catalog JSON-LD for the white-label offering itself -
// distinct from the per-plan Product/Offer schema on the Pricing page.
const AGENCY_OS_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/agency-os#service`,
  name: "Agency OS by MagicBolt AI",
  serviceType: "White-label AI automation SaaS",
  description: `${PRODUCTS_KB.find((p) => p.id === "agency").definition} It bundles every MagicBolt AI product - AI automation for Google Business Profile, Facebook, Instagram, WhatsApp and WordPress, AEO + GEO visibility and QR-based software - under the agency's own brand, domain and prices.`,
  provider: { "@type": "Organization", "@id": ORGANIZATION_ID, name: "MagicBolt Private Limited", url: SITE_URL },
  areaServed: "Worldwide",
  url: `${SITE_URL}/agency-os`,
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Agency OS product bundle",
    itemListElement: PRODUCTS_KB.filter((p) => p.status === "ACTIVE").map((p, i) => {
      const f = featureOf(p);
      return {
        "@type": "Offer",
        position: i + 1,
        itemOffered: {
          "@type": "Service",
          name: p.name,
          description: p.definition,
          ...(f ? { url: `${SITE_URL}${featurePath(f)}` } : {}),
        },
      };
    }),
  },
};

// Frameless product tile - floating round artwork over centred text, the
// same treatment as the /features grid.
function ProductCard({ kb, delay }) {
  const f = featureOf(kb);
  const body = (
    <>
      <FeatureArt
        cat={f}
        accent={badgeOf(kb)}
        alt={`${kb.name} - white-label software by MagicBolt AI`}
        className="max-w-[230px]"
      />
      <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#FF6B00]/10 text-[#FF6B00] text-[11px] font-semibold px-2.5 py-1">
        <Check className="w-3 h-3" strokeWidth={3} /> Included
      </span>
      <h4 className="mt-3 text-lg font-bold text-[#0B1533] leading-snug">{kb.name}</h4>
      <p className="mt-2 max-w-sm text-sm text-[#475569] leading-relaxed">{kb.definition}</p>
      {f && (
        <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-[#FF6B00]">
          Explore {f.title} <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      )}
    </>
  );
  const cls = "group flex h-full flex-col items-center rounded-3xl px-2 pb-4 text-center";
  return (
    <Reveal delay={delay} className="h-full">
      {f ? (
        <Link to={featurePath(f)} data-testid="product-card" className={`${cls} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]/50 focus-visible:ring-offset-4 focus-visible:ring-offset-[#F4F6FB]`}>
          {body}
        </Link>
      ) : (
        <div data-testid="product-card" className={cls}>
          {body}
        </div>
      )}
    </Reveal>
  );
}

export default function WhiteLabelPage() {
  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="whitelabel-page">
      <SEO
        title="Agency OS - White-Label SaaS Reseller Platform | MagicBolt AI"
        description="Launch your own white-label SaaS reseller platform with Agency OS: one multi-client dashboard, AI automation and sub-accounts - your brand, your prices."
        path="/agency-os"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Agency OS", path: "/agency-os" },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(AGENCY_OS_JSON_LD)}</script>
      </Helmet>
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="absolute inset-0 z-0">
          <div className="mb-blob mb-blob-orange mb-anim-a w-[380px] h-[380px] -top-24 right-0" />
          <div className="mb-blob mb-blob-blue mb-anim-b w-[320px] h-[320px] top-10 -left-20" />
          <div className="absolute inset-0 mb-grid-lines opacity-60" />
          <FloatingIcons />
        </div>

        {/* Soft white focus glow to keep text readable over icons */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] w-[860px] h-[520px] max-w-[95vw] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.7) 42%, rgba(255,255,255,0) 72%)",
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)] mb-bob">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">
                Complete white-label SaaS for agencies
              </span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]"
          >
            Agency OS :{" "}
            <span className="mb-gradient-text">White-label AI SaaS</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm md:text-base font-semibold uppercase tracking-wide text-[#64748B]"
          >
            <span>Centralized Dashboard</span>
            <span className="text-[#CBD2E0]">|</span>
            <span>AI Automation</span>
            <span className="text-[#CBD2E0]">|</span>
            <span>Grow Revenue</span>
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed"
          >
            Agency OS is MagicBolt AI's white-label SaaS platform - all{" "}
            {CLIENT_PRODUCTS.length} MagicBolt products plus the agency toolkit, sold as your own. A
            complete, 100% cloud-hosted platform your agency fully controls, with dedicated support
            behind the scenes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              data-testid="whitelabel-header-cta"
              size="lg"
              className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
              asChild
            >
              <a href={SIGNUP_URL}>
                Start your own White-label AI SaaS{" "}
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
              asChild
            >
              <Link to="/pricing">See pricing</Link>
            </Button>
          </motion.div>
          <p className="mt-3 text-sm text-[#94A3B8]">
            Using our Agency OS you can manage clients in one dashboard.{" "}
            <Link to="/how-agencies-earn" className="font-semibold text-[#FF6B00] hover:underline">
              See how agencies earn
            </Link>
          </p>
        </div>
      </section>

      {/* How it works */}
      <section data-testid="how-it-works-section" className="relative py-20 md:py-24 bg-white border-b border-[#E6E9F1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>How it works</Overline>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              How does Agency OS work?
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">
              Four steps from sign-up to selling software under your own name.
            </p>
          </Reveal>
          <ol className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06} className="h-full">
                <li className="flex h-full flex-col rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-6">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#0B1533] text-sm font-bold text-white">
                      {i + 1}
                    </span>
                    <s.icon className="w-5 h-5 text-[#FF6B00]" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-[#0B1533]">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                  <Link to={s.link.to} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline">
                    {s.link.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Products grid */}
      <section data-testid="products-section" className="relative py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              What products are included in{" "}
              <span className="text-[#FF6B00]">Agency OS?</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">
              {CLIENT_PRODUCTS.length} products your clients use, each a full product in its own right.
              Bundle them all - or your chosen mix - under a single branded login.
            </p>
          </Reveal>

          <div className="mt-14 space-y-14">
            {PRODUCT_GROUPS.map((group) => (
              <div key={group.id}>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="inline-flex items-center rounded-full bg-[#FF6B00]/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[#FF6B00]">
                    {group.name}
                  </h3>
                  <span className="h-px flex-1 bg-[#E6E9F1]" />
                </div>
                <p className="mb-8 text-sm text-[#64748B]">{group.description}</p>
                <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                  {group.items.map((kb, i) => (
                    <ProductCard key={kb.id} kb={kb} delay={(i % 3) * 0.08} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agency toolkit - the agency-platform feature pages, capability by capability */}
      <section data-testid="agency-tools-section" className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              What tools do you get to{" "}
              <span className="text-[#FF6B00]">sell, bill and scale?</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">
              Beyond the client products, Agency OS ships with the tools to run the agency itself -
              white-label control, client and plan management, billing, prospecting, one publishing
              calendar for every client, and AI that writes in each client's voice.
            </p>
          </Reveal>

          <div className="mt-14 space-y-14">
            {TOOLKIT.map((cat) => (
              <div key={cat.id}>
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div className="group flex items-center gap-4 max-w-2xl">
                    <FeatureArt cat={cat} alt={`${cat.title} - MagicBolt AI`} className="!w-24 shrink-0 md:!w-32" />
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold tracking-tight text-[#0B1533]">{cat.title}</h3>
                      <p className="mt-1 text-sm text-[#475569]">{cat.intro}</p>
                    </div>
                  </div>
                  <Link to={featurePath(cat)} className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline">
                    More on {cat.title} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <ul className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {cat.points.map((pt, i) => (
                    <Reveal key={pt.title} delay={(i % 3) * 0.06} className="h-full">
                      <li
                        data-testid="agency-tool-card"
                        className="group h-full rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:bg-white"
                      >
                        <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-[#FF6B00]/10 text-[#FF6B00] mb-4 transition-all duration-300 group-hover:bg-[#FF6B00] group-hover:text-white">
                          <pt.icon className="w-5 h-5" />
                        </div>
                        <h4 className="text-base font-semibold text-[#0B1533]">{pt.title}</h4>
                        <p className="mt-2 text-sm text-[#475569] leading-relaxed">{pt.desc}</p>
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* One platform under your brand */}
      <section className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-[#F4F6FB] overflow-hidden">
        <div className="mb-blob mb-blob-blue mb-anim-b w-[320px] h-[320px] top-10 -right-24" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <Overline>One unified platform</Overline>
              <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
                All software, one branded dashboard
              </h2>
              <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">
                Your clients log in to{" "}
                <span className="font-semibold text-[#0B1533]">your</span>{" "}
                platform, see{" "}
                <span className="font-semibold text-[#0B1533]">your</span> logo,
                and get every product working together - while MagicBolt powers
                it invisibly in the background.
              </p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {[
                  "Custom domain & branding",
                  "Unlimited products in one login",
                  "You set the prices",
                  "We handle hosting & uptime",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2.5">
                    <span className="grid place-items-center w-5 h-5 rounded-full bg-[#FF6B00]/15 text-[#FF6B00] shrink-0">
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-[#475569]">{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* Platform mock */}
            <Reveal delay={0.12}>
              <div
                data-testid="platform-mock"
                className="rounded-2xl border border-[#E6E9F1] bg-white overflow-hidden mb-card-shadow"
              >
                <div className="flex items-center justify-between px-5 h-12 border-b border-[#E6E9F1] bg-[#0B1533]">
                  <div className="flex items-center gap-2">
                    <span className="grid place-items-center w-7 h-7 rounded-md bg-[#FF6B00] text-white">
                      <Zap className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm font-semibold text-white">Your Agency OS</span>
                  </div>
                  <span className="text-[11px] text-white/50">app.youragency.com</span>
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-semibold mb-4">
                    Products live for your clients
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {CLIENT_PRODUCTS.map((kb) => (
                      <div
                        key={kb.id}
                        className="flex flex-col items-center gap-2 rounded-lg border border-[#E6E9F1] bg-[#F4F6FB] p-3 text-center"
                      >
                        <ProductBadge p={badgeOf(kb)} size="w-9 h-9" ic="w-4 h-4" />
                        <span className="text-[10px] leading-tight text-[#475569] line-clamp-2">
                          {(featureOf(kb)?.title || kb.name).replace(" Automation", "")}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-2 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-4 py-3">
                    <Cloud className="w-4 h-4 text-[#FF6B00]" />
                    <span className="text-xs text-[#0B1533]">
                      100% cloud hosted · powered by{" "}
                      <span className="text-[#FF6B00] font-semibold">you</span>
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Launch kit - what's set up for you with every plan */}
      {LAUNCH_KIT && (
        <section data-testid="launch-kit-section" className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8 grid gap-10 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter leading-tight text-[#0B1533]">
                What is set up for you at launch?
              </h2>
              <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">
                Every plan includes the launch work, so you can start selling instead of configuring -
                plus sales kits, training and lead lists listed on the pricing page.
              </p>
              <Link to="/pricing" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline">
                Everything included with every plan <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="grid gap-3 sm:grid-cols-2">
                {LAUNCH_KIT.items.map((item) => (
                  <li key={item} className="flex items-center gap-3 rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] px-4 py-3">
                    <Rocket className="w-4 h-4 shrink-0 text-[#FF6B00]" />
                    <span className="text-sm font-medium text-[#0B1533]">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section data-testid="benefits-section" className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-[#F4F6FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              A complete SaaS business, ready to sell
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-xl border border-[#E6E9F1] bg-white p-8 mb-card-shadow transition-all duration-300 hover:-translate-y-1.5 hover:border-[#FF6B00]/40 hover:mb-card-shadow-hover">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#FF6B00]/10 text-[#FF6B00] mb-6 transition-all duration-300 group-hover:bg-[#FF6B00] group-hover:text-white group-hover:scale-110">
                    <b.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0B1533]">{b.title}</h3>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Explore next */}
      <section data-testid="explore-section" className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Keep exploring</Overline>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter leading-tight text-[#0B1533]">
              Where to go next
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {EXPLORE.map((e) => (
              <Link
                key={e.to}
                to={e.to}
                className="group flex items-start justify-between gap-3 rounded-xl border border-[#E6E9F1] bg-[#F4F6FB] p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_12px_28px_rgba(11,21,51,0.10)]"
              >
                <div>
                  <div className="text-sm font-semibold text-[#0B1533]">{e.title}</div>
                  <div className="mt-1 text-xs text-[#64748B]">{e.desc}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-[#CBD2E0] transition-colors group-hover:text-[#FF6B00]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-[#E6E9F1] overflow-hidden bg-[#F4F6FB]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-20 left-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 mb-radial-glow" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              Launch your own SaaS this week
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] max-w-xl mx-auto leading-relaxed">
              Get every product, your branding and full cloud hosting - start
              reselling MagicBolt as your own today.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                asChild
              >
                <a href={SIGNUP_URL}>
                  Start your own White-label AI SaaS{" "}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
                asChild
              >
                <Link to="/pricing">Compare plans</Link>
              </Button>
            </div>
            <p className="mt-3 text-sm text-[#94A3B8]">
              Using our Agency OS you can manage clients in one dashboard.
            </p>
          </Reveal>
        </div>
      </section>

      <FaqSection title="Frequently asked questions about Agency OS" items={AGENCY_OS_FAQS} />

      <CredibilityStrip />

      <Footer showCta={false} />
    </div>
  );
}
